import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { AngularFirestore } from '@angular/fire/compat/firestore';
import { Colf } from 'npx-family-happy-common';
import { BehaviorSubject, Observable, catchError, forkJoin, map } from 'rxjs';
import { Experiences } from 'src/app/shared/models/caregiver.model';
import { environment } from 'src/environments/environment';
//CHANGE INTERFACE FROM NURSE TO COLF

const DEFAULT_FILTERS = {
  age_lower: null,
  age_upper: null,
  contract: null,
  cooking: false,
  days: null,
  doc_ref: null,
  english: false,
  fhsub: false,
  email: null,
  gardening: false,
  groceries: false,
  helping: false,
  hours: null,
  houseCleaning: false,
  ironing: false,
  italian: false,
  latitude: null,
  license: null,
  limit: 10,
  longitude: null,
  name: null,
  page: 0,
  phone: null,
  province: null,
  range: null,
  rating: false,
  sewing: false,
  spanish: false,
  taxi: false,
  taxiNoCar: false,
  terraceCleaning: false,
  washingMachines: false,
  windowCleaning: false,
};

@Injectable({
  providedIn: 'root',
})
export class ColfService {
  baseURL = `${environment.baseURL}/colfs`;
  ColfList = new BehaviorSubject<any[]>([]);

  constructor(private http: HttpClient, private firestore: AngularFirestore) {}

  checkEmptyField(field: string) {
    return field !== '' ? field : 'unknown';
  }

  getFiveSuggestions(search: string, province: string): Observable<Colf[]> {
    const filters = {
      ...DEFAULT_FILTERS,
      limit: 5,
      name: search ? search.replace(`'`, `''`) : '',
      province: province !== '' ? province : null,
    };

    return this.http.post<Colf[]>(this.baseURL + '/find', filters).pipe(
      map((ColfList) => {
        return ColfList.filter((Colf) => Colf.rating.dangerous === false).slice(
          0,
          5
        );
      })
    );
  }

  getColf(filters: any, limit: number): Observable<Colf[]> {
    const request = this.getColfFilters(filters, limit);

    this.http
      .post<Colf[]>(this.baseURL + '/find', request)
      .subscribe((data) => {
        this.ColfList.next(data);
      });

    return this.ColfList;
  }

  getWhatsappFilteredTargets(
    filters: any,
    latitude: number,
    longitude: number,
    range: number | null = 5,
    limit = 20
  ): any {
    if (filters) {
      const request = {
        age_lower: filters.fromAge ?? null,
        age_upper: filters.toAge ?? null,
        // car: filters.car ?? false,
        contract: filters.contract === '' ? null : filters.contract ?? null,
        cooking: filters.cooking ?? null,
        days: filters.days === '' ? null : filters.days ?? null,
        doc_ref: filters.docRef ?? null,
        email: filters.info.email ?? null,
        english: filters.lang.english ?? false,
        englishAdvanced: filters.lang.englishAdvanced ?? false,
        fhsub: filters.info.fhsub ?? false,
        gardening: filters.gardening ?? false,
        groceries: filters.groceries ?? false,
        helping: filters.helping ?? false,
        hours: filters.hours === '' ? null : filters.hours ?? null,
        houseCleaning: filters.houseCleaning ?? false,
        ironing: filters.ironing ?? false,
        italian: filters.lang.italian ?? false,
        italianAdvanced: filters.lang.italianAdvanced ?? false,
        license: filters.license ?? false,
        limit,
        name: null,
        page: filters.page ?? 1,
        phone: filters.info.phone ?? null,
        province: filters.province ?? null,
        rating: filters.rating.overall ?? false,
        sewing: filters.overall ?? false,
        spanish: filters.lang.spanish ?? false,
        spanishAdvanced: filters.lang.spanishAdvanced ?? false,
        terraceCleaning: filters.terraceCleaning ?? false,
        washingMachines: filters.washingMachines ?? false,
        windowCleaning: filters.windowCleaning ?? false,
        range: range ?? null,
        latitude: latitude ?? null,
        longitude: longitude ?? null,
      };
      return this.http.post<any>(
        this.baseURL + '/get-whatsapp-filtered-target',
        request
      );
    }
  }

  getListOfColfs(ids: string[]) {
    let Colfs: any = {};
    for (let i = 0; i < ids.length; i++) {
      const id = ids[i];
      Colfs[id] = this.getSingleColf(id).pipe(
        catchError((error) => {
          return error;
        })
      );
    }
    return forkJoin(Colfs);
  }

  getSingleColf(id: string) {
    const filters = {
      ...DEFAULT_FILTERS,
      doc_ref: id,
      limit: 1,
    };

    return this.http.post<Colf[]>(this.baseURL + '/find', filters);
  }

  createColf(requestData: Colf) {
    return this.http.post(this.baseURL + '/import', requestData);
  }

  updateColf(requestData: Colf) {
    return this.http.post(this.baseURL + '/import', requestData);
  }

  async getFirebaseDocRef(email: string) {
    const doc = await this.firestore
      .collection('colf')
      .add({ placeholder: email });
    return doc.id;
  }

  deleteColf(id: string) {
    // Remember to delete storage data
    return this.http.delete(this.baseURL + '/delete/' + id);
  }

  // `requestId` lets the BE apply the request flags/attributes (availability,
  // languages, ...) on top of distance, but only when `strictFilter` is true
  // (the operator explicitly wants to honour the request requirements).
  // Colf requests also return babysitters flagged "aiuto pulizie" (BE side).
  // Geo params are still sent by the FE: `province` always, `latitude` /
  // `longitude` / `range` only when a radius (in meters) is selected.
  getWhatsappTargets(
    requestId: number,
    province: string,
    latitude: number | null,
    longitude: number | null,
    range: number | null,
    subscribedOnly: boolean = true,
    strictFilter: boolean = true
  ) {
    let params =
      `requestId=${requestId}` +
      `&province=${province}` +
      `&subscribedOnly=${subscribedOnly}` +
      `&strictFilter=${strictFilter}`;
    if (latitude !== null && longitude !== null && range !== null) {
      params += `&latitude=${latitude}&longitude=${longitude}&range=${range}`;
    }
    return this.http.get<string[]>(
      `${this.baseURL}/get-whatsapp-target?${params}`
    );
  }

  /* Utilities methods */

  // It converts an object to a string. e.g.: {sun: true, mon: false, tue: true} -> 'sun-tue'
  fromAvailObjectToString(object: any, type: string) {
    let result = '';
    if (object) {
      switch (type) {
        case 'hours':
          result += object['mor'] ? 'mor' : '';
          result += object['aft'] ? (result !== '' ? '-aft' : 'aft') : '';
          result += object['eve'] ? (result !== '' ? '-eve' : 'eve') : '';
          return result;
        case 'days':
          result += object['sun'] ? 'sun' : '';
          result += object['mon'] ? (result !== '' ? '-mon' : 'mon') : '';
          result += object['tue'] ? (result !== '' ? '-tue' : 'tue') : '';
          result += object['wed'] ? (result !== '' ? '-wed' : 'wed') : '';
          result += object['thu'] ? (result !== '' ? '-thu' : 'thu') : '';
          result += object['fri'] ? (result !== '' ? '-fri' : 'fri') : '';
          result += object['sat'] ? (result !== '' ? '-sat' : 'sat') : '';
          return result;
        case 'contract':
          result += object['occ'] ? 'occ' : '';
          result += object['part'] ? (result !== '' ? '-part' : 'part') : '';
          result += object['full'] ? (result !== '' ? '-full' : 'full') : '';
          return result;
        case 'tasks1':
        case 'tasks2':
          result += object['assistance'] ? 'assistance' : '';
          result += object['care'] ? (result !== '' ? '-care' : 'care') : '';
          result += object['drugs'] ? (result !== '' ? '-drugs' : 'drugs') : '';
          result += object['cooking']
            ? result !== ''
              ? '-cooking'
              : 'cooking'
            : '';
          result += object['housing']
            ? result !== ''
              ? '-housing'
              : 'housing'
            : '';
          result += object['other'] ? (result !== '' ? '-other' : 'other') : '';
          return result;
        default:
          return '';
      }
    } else {
      return result;
    }
  }
  // It converts a string to an object (hour, contract or days property of Nurse). e.g.: 'sun-tue' -> {sun: true, mon: false, tue: true}
  fromStringToAvailObject(origin: string, type: string) {
    if (origin) {
      let keys = origin.split('-') || [];
      switch (type) {
        case 'hours':
          return {
            mor: keys.findIndex((item) => item === 'mor') !== -1,
            aft: keys.findIndex((item) => item === 'aft') !== -1,
            eve: keys.findIndex((item) => item === 'eve') !== -1,
          };
        case 'days':
          return {
            sun: keys.findIndex((item) => item === 'sun') !== -1,
            mon: keys.findIndex((item) => item === 'mon') !== -1,
            tue: keys.findIndex((item) => item === 'tue') !== -1,
            wed: keys.findIndex((item) => item === 'wed') !== -1,
            thu: keys.findIndex((item) => item === 'thu') !== -1,
            fri: keys.findIndex((item) => item === 'fri') !== -1,
            sat: keys.findIndex((item) => item === 'sat') !== -1,
          };
        case 'contract':
          return {
            occ: keys.findIndex((item) => item === 'occ') !== -1,
            part: keys.findIndex((item) => item === 'part') !== -1,
            full: keys.findIndex((item) => item === 'full') !== -1,
          };
        case 'tasks1':
        case 'tasks2':
          return {
            assistance: keys.findIndex((item) => item === 'assistance') !== -1,
            care: keys.findIndex((item) => item === 'care') !== -1,
            drugs: keys.findIndex((item) => item === 'drugs') !== -1,
            cooking: keys.findIndex((item) => item === 'cooking') !== -1,
            housing: keys.findIndex((item) => item === 'housing') !== -1,
            other: keys.findIndex((item) => item === 'other') !== -1,
          };
        default:
          return {
            occ: false,
            part: false,
            full: false,
          };
      }
    } else {
      switch (type) {
        case 'hours':
          return {
            mor: false,
            aft: false,
            eve: false,
          };
        case 'days':
          return {
            sun: false,
            mon: false,
            tue: false,
            wed: false,
            thu: false,
            fri: false,
            sat: false,
          };
        case 'contract':
          return {
            occ: false,
            part: false,
            full: false,
          };
        case 'tasks1':
        case 'tasks2':
          return {
            assistance: false,
            care: false,
            drugs: false,
            cooking: false,
            housing: false,
            other: false,
          };
        default:
          return undefined;
      }
    }
  }

  exportColfsFromDB(filters: any) {
    const request = this.getColfFilters(filters, 50);

    if (request) {
      return this.http.post(this.baseURL + '/export-colfs-to-excel', request, {
        responseType: 'blob',
      });
    }

    throw new Error('No filters provided');
  }

  private getColfFilters(filters: any, limit: number) {
    if (filters) {
      if (!filters.info) {
        filters.info = {};
      }
      if (!filters.avail) {
        filters.avail = {};
      }
      if (!filters.exp) {
        filters.exp = {};
      }
      if (!filters.lang) {
        filters.lang = {};
      }
      if (!filters.rating) {
        filters.rating = {};
      }
      if (!filters.engagement) {
        filters.engagement = {};
      }
      if (!filters.cert) {
        filters.cert = {};
      }
      return {
        age_lower: filters.fromAge ?? null,
        age_upper: filters.toAge ?? null,
        car: filters.avail.car ?? false,
        contract: filters.avail.contract ?? null,
        cooking: filters.avail.cooking ?? null,
        days: filters.avail.days ?? null,
        doc_ref: filters.docRef ?? null,
        email: filters.info.email ?? null,
        english: filters.lang.english ?? false,
        englishAdvanced: filters.lang.englishAdvanced ?? false,
        fhsub: filters.info.fhsub ?? false,
        gardening: filters.avail.gardening ?? false,
        groceries: filters.avail.groceries ?? false,
        helping: filters.avail.helping ?? false,
        hours: filters.avail.hours ?? null,
        houseCleaning: filters.avail.houseCleaning ?? false,
        ironing: filters.avail.ironing ?? false,
        italian: filters.lang.italian ?? false,
        italianAdvanced: filters.lang.italianAdvanced ?? false,
        license: filters.avail.license ?? false,
        limit,
        name: filters.info.name ? filters.info.name.replace(`'`, `''`) : null,
        page: filters.page ?? 1,
        phone: filters.info.phone ?? null,
        province: filters.province ?? null,
        rating: filters.rating.overall ?? false,
        sewing: filters.avail.overall ?? false,
        spanish: filters.lang.spanish ?? false,
        spanishAdvanced: filters.lang.spanishAdvanced ?? false,
        taxi: filters.avail.taxi ?? false, //TO CHECK--------------------------
        taxiNoCar: filters.avail.taxiNoCar ?? false,
        terraceCleaning: filters.avail.terraceCleaning ?? false,
        washingMachines: filters.avail.washingMachines ?? false,
        windowCleaning: filters.avail.windowCleaning ?? false,
        range: filters.range * 1000,
        latitude: filters.latitude ?? null,
        longitude: filters.longitude ?? null,
      };
    }
    return undefined;
  }

  addExperience(docRef: string, experience: Experiences): Observable<any> {
    return this.http.patch(`${this.baseURL}/${docRef}/experiences`, {
      experience,
    });
  }

  deleteExperience(
    docRef: string,
    cluster: string,
    index: number
  ): Observable<any> {
    return this.http.delete(
      `${this.baseURL}/${docRef}/experiences/${cluster}/${index}`
    );
  }
}
